import React from "react";
import { recentPosts } from "../../data/blogs";
import { BaseURL, BlogURL } from "../../APIs/APIUrls";
import { Link } from "react-router-dom";

const BLOGS_CACHE_KEY = "AllBlogsforWeb";
const BLOGS_CACHE_TS_KEY = "AllBlogsforWeb_TimeStamp";
const BLOGS_CACHE_TTL_MS = 5 * 60 * 1000;

const categories = [
  { name: "Market Updates", count: 50 },
  { name: "Buying Tips", count: 34 },
  { name: "Interior Inspiration", count: 69 },
  { name: "Investment Insights", count: 25 },
  { name: "Home Construction", count: 12 },
  { name: "Legal Guidance", count: 12 },
  { name: "Community Spotlight", count: 69 },
];

const tags = [
  "Property",
  "Office",
  "Finance",
  "Legal",
  "Market",
  "Invest",
  "Renovate",
];

export const formatDate = (date) => {
  if (!date) return "";

  const dateObj = new Date(date);

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  const options = { year: "numeric", month: "long", day: "numeric" };
  return dateObj.toLocaleDateString("en-US", options);
};

const returnCardImage = (propertyid, propertyImages) => {
  if (!propertyImages || !propertyImages.length) {
    return "https://via.placeholder.com/615x405?text=No+Image";
  }

  const cardImage = propertyImages.filter(
    (item) => item.IsCard === true && item.PropertyId == propertyid
  )?.[0]?.ImageURL;

  return cardImage ? BaseURL + cardImage : "https://via.placeholder.com/615x405?text=No+Image";
};

export default function Sidebar() {

  const [blogposts, setBlogposts] = React.useState([]);
  const [searchText, setSearchText] = React.useState("");

  React.useEffect(() => {
    let cacheClearTimer;

    const clearBlogsCache = () => {
      localStorage.removeItem(BLOGS_CACHE_KEY);
      localStorage.removeItem(BLOGS_CACHE_TS_KEY);
    };

    const scheduleCacheClear = (cachedAt) => {
      const expiresAt = cachedAt + BLOGS_CACHE_TTL_MS;
      const delay = Math.max(0, expiresAt - Date.now());

      if (cacheClearTimer) {
        clearTimeout(cacheClearTimer);
      }

      cacheClearTimer = setTimeout(() => {
        clearBlogsCache();
      }, delay);
    };

    const parseStoredArray = (value) => {
      try {
        const parsed = JSON.parse(value || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    };

    const loadBlogs = async () => {
      try {
        const cachedBlogs = parseStoredArray(localStorage.getItem(BLOGS_CACHE_KEY));
        const cachedTsRaw = localStorage.getItem(BLOGS_CACHE_TS_KEY);
        const cachedAt = Number(cachedTsRaw);
        const isCacheValid =
          cachedBlogs.length > 0 &&
          !Number.isNaN(cachedAt) &&
          Date.now() - cachedAt < BLOGS_CACHE_TTL_MS;

        if (isCacheValid) {
          setBlogposts(cachedBlogs);
          scheduleCacheClear(cachedAt);
          return;
        }

        clearBlogsCache();

        const response = await fetch(BlogURL + "GetAllBlogsforWeb", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        });
        if (response.ok) {
          const data = await response.json();
          const rows = data?.data || data?.Data || [];
          setBlogposts(rows);

          const now = Date.now();
          localStorage.setItem(BLOGS_CACHE_KEY, JSON.stringify(rows));
          localStorage.setItem(BLOGS_CACHE_TS_KEY, String(now));
          scheduleCacheClear(now);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    loadBlogs();

    const allProeprtiesfromCache = localStorage.getItem("AllPropertiesforWeb");
    if (allProeprtiesfromCache) {
      const parsedProperties = JSON.parse(allProeprtiesfromCache);
      setFeaturedProperties(parsedProperties.filter(p => p.IsFeatured));
    }

    return () => {
      if (cacheClearTimer) {
        clearTimeout(cacheClearTimer);
      }
    };
  }, []);

  const [featuredProperties, setFeaturedProperties] = React.useState([]);

  const filteredBlogposts = React.useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return blogposts;
    }

    return blogposts.filter((post) => {
      const title = String(post?.Title || "").toLowerCase();
      const description = String(post?.ShortDescription || "").toLowerCase();
      const author = String(post?._objAUserProfile?.Name || "").toLowerCase();
      return title.includes(query) || description.includes(query) || author.includes(query);
    });
  }, [blogposts, searchText]);

  return (
    <aside className="sidebar-blog fixed-sidebar">
      <div className="widget-search">
        <h5 className="text-black-2 text-capitalize">Search Blog</h5>
        <div className="search-box">
          <input
            className="search-field"
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <a href="#" className="icon icon-search" />
        </div>
      </div>
      <div className="widget-box recent" style={{ marginTop: "5px" }}>
        {/* <h5 className="text-black-2 text-capitalize">Featured listings</h5> */}
        <ul>
          {filteredBlogposts.slice(0, 8).map((post, index) => (
            <li key={index}>
              <div className="recent-post-item not-overlay hover-img">
                <Link to={`/blog-detail/${post.BlogId}-${post.URL}`} className="img-style">
                  <img
                    alt={post.CardImageAltText}
                    src={BaseURL + post.CardImage}
                    width={112}
                    height={74}
                  />
                </Link>
                <div className="content">
                  <Link to={`/blog-detail/${post.BlogId}-${post.URL}`} className="title link">
                    {post.Title}
                  </Link>
                  <div className="subtitle">
                    <svg
                      width={16}
                      height={17}
                      viewBox="0 0 16 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.5 2.5V4M11.5 2.5V4M2 13V5.5C2 5.10218 2.15804 4.72064 2.43934 4.43934C2.72064 4.15804 3.10218 4 3.5 4H12.5C12.8978 4 13.2794 4.15804 13.5607 4.43934C13.842 4.72064 14 5.10218 14 5.5V13M2 13C2 13.3978 2.15804 13.7794 2.43934 14.0607C2.72064 14.342 3.10218 14.5 3.5 14.5H12.5C12.8978 14.5 13.2794 14.342 13.5607 14.0607C13.842 13.7794 14 13.3978 14 13M2 13V8C2 7.60218 2.15804 7.22064 2.43934 6.93934C2.72064 6.65804 3.10218 6.5 3.5 6.5H12.5C12.8978 6.5 13.2794 6.65804 13.5607 6.93934C13.842 7.22064 14 7.60218 14 8V13"
                        stroke="#7C818B"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{formatDate(post.CreatedOn)}</span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {searchText.trim() && filteredBlogposts.length === 0 ? (
          <p className="text-variant-1 mt-12">No blogs found.</p>
        ) : null}
      </div>
      {/* <div className="widget-box categories">
        <h5 className="text-black-2 text-capitalize">Categories</h5>
        <ul className="mt-20">
          {categories.map((category, index) => (
            <li key={index}>
              <a href="#" className="categories-item link">
                <span>{category.name}</span>
                <span>({category.count})</span>
              </a>
            </li>
          ))}
        </ul>
      </div> */}
      {
        featuredProperties.length > 0 && (
          <div className="widget-box box-latest-property">
            <h5 className="fw-6 title">Featured Propeties</h5>
            <ul>
              {featuredProperties.slice(0, 6).map((property, index) => (
                <li key={index} className="latest-property-item">
                  <Link
                    to={`/property-details/${property.AutoGeneratedURL}`}
                    className="images-style"
                  >
                    <img
                      data-src={returnCardImage(property.PropertyId, property._lstPropertyImages)}
                      alt={property?.PropertyTitle}
                      src={returnCardImage(property.PropertyId, property._lstPropertyImages)}
                      width={615}
                      height={405}
                      loading="lazy"
                    />
                  </Link>
                  <div className="content">
                    <div className="text-capitalize text-btn">
                      <Link
                        to={`/property-details/${property.AutoGeneratedURL}`}
                        className="link"
                      >
                        {property.PropertyGeneratedId} &nbsp;&nbsp;|&nbsp;&nbsp;{property.PropertyFor} &nbsp;&nbsp;|&nbsp;&nbsp; {property.PropertyTitle}
                      </Link>
                    </div>
                    <ul className="meta-list mt-6">
                      {
                        property.NoofRoom && (
                          <>
                            <li className="item">
                              <i className="icon icon-bed" />
                              <span className="text-variant-1">Beds:</span>
                              <span className="fw-6">{property.NoofRoom}</span>
                            </li>
                          </>
                        )
                      }
                      {
                        property.NoofWashroom && (
                          <li className="item">
                            <i className="icon icon-bath" />
                            <span className="text-variant-1">Baths:</span>
                            <span className="fw-6">{property.NoofWashroom}</span>
                          </li>

                        )
                      }
                      {
                        property.PropertyFor === "Sell" && (
                          <li className="item">
                            <i className="icon icon-sqft" />
                            <span className="fw-6">Land Area: {property.LandSize}</span>
                            <span className="text-variant-1">{property.LandSizeUnit}</span>

                          </li>
                        )
                      }
                      {
                        (property.PropertyFor === "Rent" || property.PropertyFor === "PG") && property.CarpetArea ?
                          (property.PropertyFor === "Rent" || property.PropertyFor === "PG") && property.CarpetArea && (
                            <li className="item">
                              <i className="icon icon-sqft" />

                              <span className="fw-6">Carpet Area: {property.CarpetArea}</span>
                              <span className="text-variant-1">{property.CarpetAreaUnitSize}</span>
                            </li>
                          ) :
                          (property.PropertyFor === "Rent" || property.PropertyFor === "PG") && property.BuiltUpArea ?
                            (property.PropertyFor === "Rent" || property.PropertyFor === "PG") && property.BuiltUpArea && (
                              <li className="item">
                                <i className="icon icon-sqft" />
                                <span className="fw-6">Constructed Area: {property.BuiltUpArea}</span>
                                <span className="text-variant-1">{property.BuiltUpAreaUnitSize}</span>
                              </li>
                            ) :
                            (property.PropertyFor === "Rent" || property.PropertyFor === "PG") && property.SuperArea ?
                              (property.PropertyFor === "Rent" || property.PropertyFor === "PG") && property.SuperArea && (
                                <li className="item">
                                  <i className="icon icon-sqft" />
                                  <span className="fw-6">Constructed Area: {property.SuperArea}</span>
                                  <span className="text-variant-1">{property.SuperAreaUnitSize}</span>
                                </li>
                              ) : null
                      }
                    </ul>
                    <div className="mt-10 text-btn">
                      {
                        property.PropertyFor == "Rent" || property.PropertyFor == "PG"
                          ? `₹ ${new Intl.NumberFormat('en-IN').format(property.RentPerMonth)} /Month`
                          :
                          property.PropertyFor == "Sell" ?
                            `₹ ${new Intl.NumberFormat('en-IN').format(property.ExpectedPrice)} /${property.ExpectedPricePerUnit}`
                            : null
                      }
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )
      }

      {/* <div className="widget-box recent">
        <h5 className="text-black-2 text-capitalize">Featured listings</h5>
        <ul>
          {recentPosts.map((post, index) => (
            <li key={index}>
              <div className="recent-post-item not-overlay hover-img">
                <a href="blog-detail" className="img-style">
                  <img
                    alt="post-recent"
                    src={post.imgSrc}
                    width={112}
                    height={74}
                  />
                </a>
                <div className="content">
                  <a href="blog-detail" className="title link">
                    {post.title}
                  </a>
                  <div className="subtitle">
                    <svg
                      width={16}
                      height={17}
                      viewBox="0 0 16 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.5 2.5V4M11.5 2.5V4M2 13V5.5C2 5.10218 2.15804 4.72064 2.43934 4.43934C2.72064 4.15804 3.10218 4 3.5 4H12.5C12.8978 4 13.2794 4.15804 13.5607 4.43934C13.842 4.72064 14 5.10218 14 5.5V13M2 13C2 13.3978 2.15804 13.7794 2.43934 14.0607C2.72064 14.342 3.10218 14.5 3.5 14.5H12.5C12.8978 14.5 13.2794 14.342 13.5607 14.0607C13.842 13.7794 14 13.3978 14 13M2 13V8C2 7.60218 2.15804 7.22064 2.43934 6.93934C2.72064 6.65804 3.10218 6.5 3.5 6.5H12.5C12.8978 6.5 13.2794 6.65804 13.5607 6.93934C13.842 7.22064 14 7.60218 14 8V13"
                        stroke="#7C818B"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{post.date}</span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="widget-box newsletter">
        <h5 className="text-black-2 text-capitalize">Join our newsletter</h5>
        <p className="caption-2 text-variant-1 mt-20">
          Signup to be the first to hear about exclusive deals, special offers
          and upcoming collections
        </p>
        <div className="search-box mt-20">
          <input
            className="search-field"
            type="text"
            placeholder="Enter your email"
          />
          <a href="#" className="icon">
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.00035 7.99998L2.17969 2.08398C6.53489 3.35043 10.6419 5.35118 14.3237 7.99998C10.6422 10.6492 6.53541 12.6504 2.18035 13.9173L4.00035 7.99998ZM4.00035 7.99998H9.00035"
                stroke="#1563DF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
      <div className="widget-box tag">
        <h5 className="text-black-2 text-capitalize">Popular Tag</h5>
        <ul className="mt-20">
          {tags.map((tag, index) => (
            <li key={index}>
              <a href="#" className="tag-item">
                {tag}
              </a>
            </li>
          ))}
        </ul>
      </div> */}
    </aside>
  );
}
